import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Heading,
  I,
  InfoCallout,
  Stack,
} from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { LoopVideo } from '@/components/media/LoopVideo';
import welcomeMp4 from '@/assets/onboarding/welcome.mp4';
import welcomeWebm from '@/assets/onboarding/welcome.webm';
import welcomePoster from '@/assets/onboarding/welcome-poster.png?w=440;880;1320&format=avif;webp;png&as=picture';

const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: 'Find a Circle to join' },
  { label: 'Connect with your community' },
  { label: 'Build leadership skills' },
  { label: "You're in" },
];

export const Route = createFileRoute('/onboarding/cm/welcome')({
  component: CMOnboardingWelcomePage,
});

function CMOnboardingWelcomePage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role="Circle Member"
      stepIndex={0}
      steps={STEPS}
      minutes={3}
      title="Welcome to the Lean In community — we are SO glad you’re here."
      primary={
        <Button
          variant="primary"
          icon={I.arrow}
          onClick={() => {
            trackOnboardingStepCompleted(
              flowStepParams('/onboarding/cm/welcome'),
            );
            navigate({ to: '/onboarding/cm/find-circle' });
          }}
        >
          Get started
        </Button>
      }
    >
      <Stack gap="lg">
        <LoopVideo
          webm={welcomeWebm}
          mp4={welcomeMp4}
          poster={welcomePoster}
          aria-label="Welcome to Lean In Circles"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        <Stack gap="md">
          <Heading level={2} style={{ fontStyle: 'italic' }}>
            Dear Circle Member,
          </Heading>
          <Body size="md">
            You just joined a Circle. That’s a big step — and it might end up
            changing more than you expect.
          </Body>
          <Body size="md">
            Whether you joined looking for support, connection, career growth,
            or simply a space where you can be yourself, you’re in the right
            place. And you’re definitely not alone.
          </Body>
          <Body size="md">
            Women in Lean In Circles are nearly{' '}
            <strong style={{ color: 'var(--primary)' }}>
              2x as likely to get promoted or receive a raise
            </strong>
            . They’re also far more likely to have mentors, sponsors, and the
            support they need to grow their careers. But what members talk
            about most isn’t just career growth — it’s the feeling of finally
            finding their people.
          </Body>

          <Body size="md">One Circle member shared:</Body>
          <InfoCallout tone="lilac" pad="default">
            <em>
              “As a stay-at-home mom, I felt isolated. Joining a Lean In Circle
              connected me with like-minded women, providing a sense of
              belonging and support. The Circle became my tribe.”
            </em>
          </InfoCallout>

          <Body size="md">Another said:</Body>
          <InfoCallout tone="lilac" pad="default">
            <em>
              “My Circle encouraged me to ask for a promotion and even helped
              me practice my pitch. I nailed it and got the raise I deserved!”
            </em>
          </InfoCallout>

          <Body size="md">
            Your Circle doesn’t need perfection to work. You don’t need to
            have everything figured out. You just need to show up — as
            yourself.
          </Body>
          <Body size="md">
            Over the coming weeks, you’ll build relationships, share
            experiences, support one another, and grow together. Some
            conversations may stay with you for years.
          </Body>
          <Body size="md">We’re so glad you’re here.</Body>
          <Body size="md">— The Lean In Team</Body>
        </Stack>
      </Stack>
    </FlowShell>
  );
}
